/** LaunchDarkly / product channel for copy-generator context (email vs SMS). */
export type CopyGeneratorChannel = 'email' | 'sms';

export type ChatMessageRole = 'assistant' | 'user';

export type ChatMessageFeedback = {
    rating: 'like' | 'dislike';
    tags: string[] | null;
    reviewText: string | null;
};

export type ChatMessage = {
    /** SERIAL primary key of the persisted message row. Present on all messages returned by the backend. */
    id: number;
    content: string;
    role: ChatMessageRole;
    /** Existing feedback for this message submitted by the current user. Null if none. */
    feedback?: ChatMessageFeedback | null;
};

/** Request body for POST /copy-generator/generate */
export type GenerateCopyRequest = {
    /** Session ID from a previous call. Omit on first message — backend creates a new session. */
    sessionId?: number;
    channel: CopyGeneratorChannel;
    /** The new user message text only. Prior history is managed server-side. */
    message: string;
    /**
     * ID of the content template currently open in the editor.
     * Passed on every request so the backend can resume the last session for this template.
     * Omit (or pass null) when creating new content — backend always starts a fresh session.
     */
    templateId?: number | null;
};

/** Response from POST /copy-generator/generate */
export type GenerateCopyResponse = {
    sessionId: number;
    message: {
        id: number;
        role: 'assistant';
        content: string;
    };
};

/**
 * @deprecated No longer used as the API request shape.
 * Kept only for any legacy call sites that have not been migrated.
 */
export type CopyGeneratorChatState = {
    channel: CopyGeneratorChannel;
    messages: ChatMessage[];
};

/** Mint fill for user outgoing bubbles (Figma: Other/Mint). */
export const COPY_GENERATOR_USER_BUBBLE_BG = 'other.mint';

/**
 * API `errorCode` when the org has not completed brand registration for AI copy tools.
 * Matches backend: `{ "message": "Brand not registered", "errorCode": "638bbb87-6a99-4ad0-933d-2ec972cfb817" }`
 */
export const COPY_GENERATOR_ERROR_CODE_BRAND_NOT_REGISTERED = '638bbb87-6a99-4ad0-933d-2ec972cfb817';

/** Figma Content-Builder / Other — Water (empty-state panel). */
export const COPY_GENERATOR_BRAND_REQUIRED_PANEL_BG = 'other.water';

export type MessageFeedbackRating = 'like' | 'dislike';

export const COPY_GENERATOR_FEEDBACK_TAGS = ['inaccurate', 'inappropriate', 'offensive'] as const;
export type FeedbackTag = typeof COPY_GENERATOR_FEEDBACK_TAGS[number];

export type MessageFeedbackBody = {
    rating: MessageFeedbackRating;
    tags?: FeedbackTag[];
    reviewText?: string;
};
